import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, Select, MenuItem, FormControl, FormHelperText, OutlinedInput } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DateTimeInput from "components/MDDateTimeInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import FormField from "./FormField";
import { ranuButtonSx, ranuOutlinedButtonSx } from "./SectionCard";
import { confirmAction, digitsOnly, getFormErrors, hasErrors, labelOf } from "./FormUtil";

const ERROR_RED = "red";

// Red border helper, used for the phone field when it has an error
const errorBorderSx = (error) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
});

// Render order and rules of the form
const FIELDS = [
  { name: "first_name", required: true },
  { name: "middle_name" },
  { name: "last_name", required: true },
  { name: "email" },
  { name: "phone", required: true },
  { name: "gender" },
  { name: "birth_date" },
  { name: "anniversary_date", type: "date" },
  { name: "comments", full: true },
];

const DevoteeForm = ({
  devoteeData,
  countryList,
  currentSelected,
  setCurrentSelected,
  onSubmit,
  onUndoChanges,
  isEdit,
  isConfirmed,
}) => {
  const [formData, setFormData] = useState(devoteeData);
  const [hasChanges, setHasChanges] = useState(false);
  const [touched, setTouched] = useState({});

  const resetForm = useCallback(() => {
    setFormData(devoteeData);
    setHasChanges(false);
    setTouched({});
  }, [devoteeData]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  /* ------------------------------- Validation ------------------------------ */
  // Real validation state: used to enable / disable the submit button
  const errors = getFormErrors(FIELDS, formData);
  const isFormValid = !hasErrors(errors);

  // What the user sees: an error only appears once the field has been touched
  const getVisibleError = (name) => (touched[name] ? errors[name] : "");

  /* -------------------------------- Handlers ------------------------------- */
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "phone" ? digitsOnly(value, 10) : value,
    }));
    setHasChanges(true);
  };

  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleCountryChange = (event) => {
    const selected = countryList.find((country) => country.value === event.target.value);
    setHasChanges(true);
    setCurrentSelected(selected);
  };

  const handleUndo = () => {
    resetForm();
    onUndoChanges();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Show every error (if any) when the user tries to submit
    if (!isFormValid) {
      const allTouched = {};
      FIELDS.forEach((field) => {
        allTouched[field.name] = true;
      });
      setTouched(allTouched);
      return;
    }

    // If no changes, just confirm without API call
    if (!hasChanges) {
      onSubmit(formData, hasChanges);
      return;
    }

    const confirmed = await confirmAction({
      title: isEdit ? "Update Devotee?" : "Add New Devotee?",
      text: isEdit
        ? "Are you sure you want to update this devotee?"
        : "Are you sure you want to add this new devotee?",
    });

    if (confirmed) {
      onSubmit(formData, hasChanges);
    }
  };

  /* -------------------------------- Renderers ------------------------------ */
  const renderError = (error) => error && <FormHelperText error>{error}</FormHelperText>;

  const renderGender = ({ name }) => {
    const error = getVisibleError(name);

    return (
      <FormControl fullWidth error={!!error}>
        <Select
          style={{ width: "100%", height: "45px" }}
          value={formData[name] ?? ""}
          onChange={(e) => {
            handleChange(name, Number(e.target.value));
            handleBlur(name);
          }}
          onBlur={() => handleBlur(name)}
          disabled={isConfirmed}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select Gender
          </MenuItem>
          <MenuItem value={0}>Male</MenuItem>
          <MenuItem value={1}>Female</MenuItem>
        </Select>
        {renderError(error)}
      </FormControl>
    );
  };

  // Country code + phone number: tinted "+91" block inside the input (same as Add Devotee)
  const renderPhone = ({ name }) => {
    const error = getVisibleError(name);

    return (
      <>
        <FormControl fullWidth error={!!error}>
          <OutlinedInput
            autoComplete="off"
            placeholder="Enter phone"
            value={formData[name] ?? ""}
            onChange={(e) => handleChange(name, e.target.value)}
            onBlur={() => handleBlur(name)}
            disabled={isConfirmed}
            sx={({ palette }) => ({
              height: "45px",
              paddingLeft: 0,
              ...errorBorderSx(error),
              ...(isConfirmed && { backgroundColor: palette.grey[200] }),
            })}
            startAdornment={
              // Highlighted country-code block: tinted background, centered, line on the right
              <MDBox
                sx={({ palette }) => ({
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "95px",
                  mr: 1.5,
                  backgroundColor: alpha(palette.primary.main, 0.08),
                  borderRight: `1px solid ${alpha(palette.primary.main, 0.35)}`,
                  borderRadius: "9px 0 0 9px",
                })}
              >
                <Select
                  variant="standard"
                  disableUnderline
                  value={currentSelected.value}
                  onChange={handleCountryChange}
                  disabled={isConfirmed}
                  // Box shows only the code (+91); the dropdown shows "INDIA : +91"
                  renderValue={() => String(currentSelected.label).split(":").pop().trim()}
                  sx={({ palette }) => ({
                    width: "100%",
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 0,
                      pl: 1.5,
                      fontWeight: 700,
                      color: palette.primary.main,
                      textAlign: "center",
                      backgroundColor: "transparent !important",
                    },
                    "& .MuiSelect-icon": { color: palette.primary.main },
                  })}
                >
                  {countryList.length === 0 && (
                    <MenuItem value={currentSelected.value}>{currentSelected.label}</MenuItem>
                  )}
                  {countryList.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            }
          />
        </FormControl>
        {renderError(error)}
      </>
    );
  };

  const renderInput = ({ name, type }) => {
    const error = getVisibleError(name);
    const isDate = type === "date";

    let placeholder = `Enter ${labelOf(name)}`;
    if (isDate) placeholder = `Select ${labelOf(name)}`;
    if (name === "comments") placeholder = "Enter any additional comments";

    return (
      <>
        <DateTimeInput
          type={isDate ? "date" : "text"}
          autoComplete="off"
          value={formData[name] ?? ""}
          onChange={(e) => handleChange(name, e.target.value)}
          onBlur={() => handleBlur(name)}
          error={!!error}
          fullWidth
          disabled={isConfirmed}
          placeholder={placeholder}
        />
        {renderError(error)}
      </>
    );
  };

  const renderControl = (field) => {
    switch (field.name) {
      case "gender":
        return renderGender(field);
      case "phone":
        return renderPhone(field);
      default:
        return renderInput(field);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate autoComplete="off">
      <Grid container spacing={2}>
        {FIELDS.map((field) => (
          <FormField key={field.name} name={field.name} required={field.required} full={field.full}>
            {renderControl(field)}
          </FormField>
        ))}

        <Grid item xs={12}>
          <MDBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={2}>
            {hasChanges && (
              <MDButton
                type="button"
                variant="outlined"
                color="error"
                onClick={handleUndo}
                sx={ranuOutlinedButtonSx}
              >
                Cancel
              </MDButton>
            )}
            <MDButton
              type="submit"
              color="primary"
              sx={ranuButtonSx}
              disabled={isConfirmed && !hasChanges}
            >
              {isEdit ? (hasChanges ? "Update Devotee" : "Confirm") : "Add Devotee"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </form>
  );
};

DevoteeForm.propTypes = {
  devoteeData: PropTypes.shape({
    first_name: PropTypes.string,
    middle_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gender: PropTypes.oneOf([0, 1, 2]),
    birth_date: PropTypes.string,
    anniversary_date: PropTypes.string,
    comments: PropTypes.string,
  }).isRequired,
  countryList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentSelected: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  setCurrentSelected: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUndoChanges: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isConfirmed: PropTypes.bool.isRequired,
};

export default DevoteeForm;
